import React, { useEffect, useState } from 'react';
import {
    CheckCircle2, CircleOff, Eye, EyeOff, Image as ImageIcon, NotebookPen, PenLine, Plus, Search, Sparkles, Trash2, UploadCloud, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import ConfirmDialog from '../../components/ConfirmDialog';
import DatePicker from '../../components/DatePicker';
import API from '../../utils/api';
import '../Faqs/Faqs.css';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './Blogs.css';

const BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const mediaUrl = path => (path && path.startsWith('/') ? `${BASE}${path}` : path);
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { title: '', excerpt: '', content: '', coverImage: '', category: 'General', author: 'Gettimelam Team', isPublished: true, publishedAt: today() };
const formatDate = value => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const PAGE_SIZE = 10;

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [categories, setCategories] = useState([]);
    const [total, setTotal] = useState(0);
    const [allTotal, setAllTotal] = useState(0);
    const [published, setPublished] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = async (targetPage = 1, append = false) => {
        try {
            if (append) setLoadingMore(true);
            else setLoading(true);
            const params = { limit: PAGE_SIZE, page: targetPage };
            if (search.trim()) params.search = search.trim();
            const res = await API.get('/admin/blogs', { params });
            setBlogs(current => append ? [...current, ...(res.data.blogs || [])] : (res.data.blogs || []));
            setCategories(res.data.categories || []);
            setTotal(res.data.total || 0);
            setAllTotal(res.data.allTotal || 0);
            setPublished(res.data.published || 0);
            setPage(res.data.page || targetPage);
            setHasMore(Boolean(res.data.hasMore));
        } catch (error) {
            toast.error('Failed to load blog posts');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };
    useEffect(() => { load(); }, [search]);
    const shown = blogs;

    const update = (field, value) => setForm(current => ({ ...current, [field]: value }));
    const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

    const startEdit = async blog => {
        try {
            const res = await API.get(`/admin/blogs/${blog._id}`);
            const full = res.data.blog;
            setForm({
                title: full.title, excerpt: full.excerpt || '', content: full.content, coverImage: full.coverImage || '',
                category: full.category || 'General', author: full.author || 'Gettimelam Team', isPublished: full.isPublished,
                publishedAt: String(full.publishedAt || '').slice(0, 10) || today(),
            });
            setEditingId(blog._id);
            setShowForm(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            toast.error('Could not open the post');
        }
    };

    const uploadCover = async event => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!checkFiles([file], RULES.photo).length) return;
        setUploading(true);
        const data = new FormData();
        data.append('coverImage', file);
        try {
            const res = await API.post('/admin/blogs/upload-cover', data);
            update('coverImage', res.data.coverImage);
            toast.success('Image uploaded');
        } catch (error) {
            toast.error(uploadFailMessage(error, [file], RULES.photo));
        } finally {
            setUploading(false);
        }
    };

    const submit = async event => {
        event.preventDefault();
        if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content are required'); return; }
        setSaving(true);
        try {
            if (editingId) await API.put(`/admin/blogs/${editingId}`, form);
            else await API.post('/admin/blogs', form);
            toast.success(editingId ? 'Post updated' : 'Post created');
            closeForm();
            load();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save the post');
        } finally {
            setSaving(false);
        }
    };

    const togglePublished = async blog => {
        try {
            const res = await API.put(`/admin/blogs/${blog._id}/toggle`);
            toast.success(res.data.message);
            load();
        } catch (error) {
            toast.error('Failed to update the post');
        }
    };

    const confirmDelete = async () => {
        const id = pendingDelete;
        setPendingDelete(null);
        if (!id) return;
        try {
            await API.delete(`/admin/blogs/${id}`);
            toast.success('Post deleted');
            load();
        } catch (error) {
            toast.error('Failed to delete the post');
        }
    };

    return (
        <div className="faqs-layout">
            <Sidebar />
            <div className="admin-content faqs-content">
                <Navbar title={<span className="faqs-nav-title"><NotebookPen size={18} /> Blog management</span>} />

                <main className="faqs-page">
                    <header className="faqs-hero">
                        <div className="faqs-hero__copy">
                            <span className="faqs-eyebrow"><Sparkles size={14} /> Content</span>
                            <h1>Blog posts</h1>
                            <p>Write articles for families. Published posts appear on the website home page, the Blog page and their own post page. Drafts stay hidden.</p>
                        </div>
                        <div className="faqs-hero__stats">
                            <div><strong>{allTotal}</strong><span>Total</span></div>
                            <div><strong>{published}</strong><span>Published</span></div>
                            <div><strong>{allTotal - published}</strong><span>Drafts</span></div>
                        </div>
                    </header>

                    <section className="faqs-toolbar">
                        <label className="faqs-search">
                            <Search size={15} />
                            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search posts" aria-label="Search posts" />
                            {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={13} /></button>}
                        </label>
                        <button type="button" className={`faqs-add ${showForm ? 'is-cancel' : ''}`} onClick={() => (showForm ? closeForm() : setShowForm(true))}>
                            {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> New post</>}
                        </button>
                    </section>

                    {showForm && (
                        <form className="faq-form" onSubmit={submit}>
                            <header>
                                <span><NotebookPen size={18} /></span>
                                <div>
                                    <h2>{editingId ? 'Edit post' : 'Write a new post'}</h2>
                                    <p>Use the formatting tips under the content box. The post address is made from the title.</p>
                                </div>
                            </header>

                            <div className="faq-form__grid">
                                <label className="faq-field faq-field--wide">
                                    <span>Title <b>*</b></span>
                                    <input value={form.title} maxLength={200} onChange={event => update('title', event.target.value)} placeholder="Example: Jain Matrimony in Delhi: Connecting Hearts" required />
                                </label>

                                <div className="faq-field faq-field--wide">
                                    <span><ImageIcon size={13} /> Cover image <small>JPG or PNG, max 5MB</small></span>
                                    {form.coverImage ? (
                                        <div className="blog-cover">
                                            <SafeImage src={mediaUrl(form.coverImage)} alt="Cover preview" />
                                            <button type="button" onClick={() => update('coverImage', '')}><Trash2 size={14} /> Remove</button>
                                        </div>
                                    ) : (
                                        <label className={`blog-upload ${uploading ? 'is-loading' : ''}`}>
                                            <input type="file" accept="image/*" disabled={uploading} onChange={uploadCover} />
                                            <UploadCloud size={24} />
                                            <strong>{uploading ? 'Uploading…' : 'Upload cover image'}</strong>
                                            <small>A wide image (about 1200 × 630) looks best</small>
                                            <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.photo)}</small>
                                        </label>
                                    )}
                                </div>

                                <label className="faq-field faq-field--wide">
                                    <span>Short summary <small>{form.excerpt.length}/400</small></span>
                                    <textarea value={form.excerpt} maxLength={400} rows={2} onChange={event => update('excerpt', event.target.value)} placeholder="One or two lines shown on the home page card" />
                                </label>

                                <label className="faq-field faq-field--wide">
                                    <span>Content <b>*</b></span>
                                    <textarea className="blog-content" value={form.content} rows={16} onChange={event => update('content', event.target.value)} placeholder={'Write your article here.\n\n## A section heading\n\nA paragraph of text.\n\n- A bullet point\n- Another bullet point'} required />
                                    <small className="faq-hint">
                                        Formatting: start a line with <code>## </code> for a heading, <code>### </code> for a smaller heading, <code>- </code> for a bullet,
                                        <code> 1. </code> for a numbered list, <code>&gt; </code> for a quote. Leave a blank line between paragraphs. Put **double stars** around text to make it bold.
                                    </small>
                                </label>

                                <label className="faq-field">
                                    <span>Category</span>
                                    <input list="blog-categories" value={form.category} maxLength={60} onChange={event => update('category', event.target.value)} placeholder="General" />
                                    <datalist id="blog-categories">{categories.map(name => <option key={name} value={name} />)}</datalist>
                                </label>
                                <label className="faq-field">
                                    <span>Author</span>
                                    <input value={form.author} maxLength={80} onChange={event => update('author', event.target.value)} />
                                </label>
                                <label className="faq-field">
                                    <span>Publish date</span>
                                    <DatePicker value={form.publishedAt} onChange={value => update('publishedAt', value)} />
                                </label>
                                <div className="faq-field faq-checks">
                                    <label><input type="checkbox" checked={form.isPublished} onChange={event => update('isPublished', event.target.checked)} /> Published (visible on the website)</label>
                                    <small className="faq-hint">Untick to save it as a draft.</small>
                                </div>
                            </div>

                            <footer>
                                <span><CheckCircle2 size={15} /> Changes appear on the website straight away.</span>
                                <div>
                                    <button type="button" className="secondary" onClick={closeForm}>Cancel</button>
                                    <button type="submit" className="primary" disabled={saving}>
                                        {editingId ? <PenLine size={15} /> : <Plus size={15} />} {saving ? 'Saving…' : editingId ? 'Save changes' : 'Publish post'}
                                    </button>
                                </div>
                            </footer>
                        </form>
                    )}

                    {shown.length > 0 ? (
                        <>
                        <ul className="blog-list">
                            {shown.map(blog => (
                                <li key={blog._id} className={`blog-item ${blog.isPublished ? '' : 'is-draft'}`}>
                                    <div className="blog-item__thumb">
                                        {blog.coverImage ? <SafeImage src={mediaUrl(blog.coverImage)} alt="" /> : <ImageIcon size={22} />}
                                    </div>
                                    <div className="blog-item__body">
                                        <div className="faq-card__chips">
                                            <span className="chip chip--cat">{blog.category || 'General'}</span>
                                            <span className={`chip ${blog.isPublished ? 'chip--on' : 'chip--off'}`}>
                                                {blog.isPublished ? <CheckCircle2 size={11} /> : <CircleOff size={11} />} {blog.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </div>
                                        <h3>{blog.title}</h3>
                                        <p>{blog.excerpt || 'No summary'}</p>
                                        <small>{formatDate(blog.publishedAt)} · {blog.author} · /blog/{blog.slug}</small>
                                    </div>
                                    <div className="faq-card__actions">
                                        <button type="button" title="Edit" onClick={() => startEdit(blog)}><PenLine size={15} /></button>
                                        <button type="button" title={blog.isPublished ? 'Move to drafts' : 'Publish'} onClick={() => togglePublished(blog)}>{blog.isPublished ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                                        <button type="button" title="Delete" className="danger" onClick={() => setPendingDelete(blog._id)}><Trash2 size={15} /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {hasMore && <div className="faqs-load-more"><button type="button" disabled={loadingMore} onClick={() => load(page + 1, true)}>{loadingMore ? 'Loading...' : `Load more (${Math.max(total - blogs.length, 0)} remaining)`}</button></div>}
                        </>
                    ) : !loading && (
                        <section className="faqs-empty">
                            <span><NotebookPen size={30} /></span>
                            <h2>{blogs.length === 0 ? 'No blog posts yet' : 'No posts match your search'}</h2>
                            <p>{blogs.length === 0 ? 'Write your first article. It will appear on the website home page and the Blog page.' : 'Try a different word.'}</p>
                            {blogs.length === 0 && <button type="button" onClick={() => setShowForm(true)}><Plus size={16} /> Write first post</button>}
                        </section>
                    )}
                </main>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete post?"
                message="This post will be permanently removed from the website."
                confirmLabel="Delete post"
                danger
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default Blogs;
